"use client";

import { ArrowRight, Facebook, Instagram, Linkedin, Loader, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactBlocks = [{
  label: "CONSULTATION",
  primary: "+357 96 23 11 06",
  primaryType: "phone",
  primaryValue: "+357 96 23 11 06",
  secondary: "Mon-Fri: 8am - 7pm",
  tertiary: "Sat: Closed | Sun: Closed"
}, {
  label: "SUPPORT",
  primary: "info@infinix-solutions.org",
  primaryType: "email",
  primaryValue: "info@infinix-solutions.org",
  secondary: "Technical & data inquiries",
  tertiary: "Response within 4 hours"
}, {
  label: "ADRESS",
  primary: "2401 Egomi",
  primaryType: "text",
  primaryValue: "",
  secondary: "Nicosia",
  tertiary: "Cyprus"
}];
const socials = [{
  name: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  name: "Twitter",
  icon: X,
  href: "#"
}, {
  name: "Facebook",
  icon: Facebook,
  href: "#"
}, {
  name: "LinkedIn",
  icon: Linkedin,
  href: "https://www.linkedin.com/company/infinix-solutions-cyprus"
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card rounded-lg">
        <p className="font-default text-xs uppercase tracking-widest text-primary mb-3">Request Received</p>
        <p className="font-default text-foreground text-lg mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-border text-foreground hover:bg-primary hover:text-white">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Jane Smith" className="rounded-lg border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="jane@company.com" className="rounded-lg border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+1 555-0000" className="rounded-lg border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <p className="font-default text-xs text-muted-foreground mt-1">Include country code for international inquiries.</p>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="contact_method" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Preferred Contact Method</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="rounded-lg border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default w-full">
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent className="rounded-lg bg-popover border-border">
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Phone">Phone</SelectItem>
                  <SelectItem value="No Preference">No Preference</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Project Details</FormLabel>
            <FormControl>
              <Textarea {...field} rows={4} placeholder="Describe your data requirements..." className="rounded-lg border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default placeholder:text-muted-foreground/50 resize-none min-h-[110px]" />
            </FormControl>
            <p className="font-default text-xs text-muted-foreground mt-2">Provide details such as scope, timeline, or specific goals.</p>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-lg">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4 border-t border-border">
        <div className="flex items-center gap-3">
          <Checkbox id="atelier_subscribe" className="rounded-lg border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white" />
          <Label htmlFor="atelier_subscribe" className="font-default text-sm text-muted-foreground cursor-pointer">
            Subscribe to industry insights
          </Label>
        </div>
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-primary text-white hover:bg-primary/90 shadow-lg h-12 px-8 font-default uppercase tracking-[0.2em] text-xs">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : <>
              Send Request
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>

      <p className="font-default text-xs text-muted-foreground pt-2">
        Your request is handled by our consulting team. Privacy is guaranteed.
      </p>
    </Form>;
}
export default function StartAConversation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3336" id="start-a-conversation" className="text-foreground py-24 md:py-[200px] px-6 md:px-12 lg:px-16 overflow-hidden dark bg-accent">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-16 md:mb-24">
          
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 flex flex-col">
            <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-6xl text-foreground leading-[0.95] mb-12 tracking-tight">
              Connect with<br />Infinix Experts.
            </h2>

            <div className="flex flex-col">
              {contactBlocks.map((block, i) => <div data-index={i} key={block.label} className="py-8 border-t border-border first:border-t-0 first:pt-0">
                  <p className="font-default text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
                    {block.label}
                  </p>
                  {block.primaryType === "phone" || block.primaryType === "email" ? <Link to={block.primaryValue} className="font-default text-base text-foreground hover:text-primary transition-colors block mb-2">
                      {block.primary}
                    </Link> : <p className="font-default text-base text-foreground mb-2">{block.primary}</p>}
                  <p className="font-default text-sm text-muted-foreground">{block.secondary}</p>
                  <p className="font-default text-sm text-muted-foreground">{block.tertiary}</p>
                </div>)}
            </div>

            <div className="flex items-center gap-6 mt-12 pt-8 border-t border-border">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link data-index={i} key={social.name} to={social.href} className="text-foreground hover:text-primary transition-colors">
                    <Icon className="size-[22px]" strokeWidth={1} />
                  </Link>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -top-2 -right-2 w-12 h-px bg-primary"></div>
              <div className="absolute -top-2 -right-2 w-px h-12 bg-primary"></div>

              <div className="backdrop-blur-sm border border-border shadow-lg p-8 md:p-12 lg:p-16 rounded-lg bg-accent">
                <FormProvider formId="start_conversation_form_get_in_touch" sectionName="StartAConversation" defaultValues={{
                name: "",
                email: "",
                phone: "",
                contact_method: "",
                message: ""
              }}>
                  <InquiryForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}